import React, { useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import { guestService } from 'api/services/guestService';
import type { BookingGuestRequest } from 'api/types/booking.types';
import { isGuestNameRequired } from '../booking.utils';

// ============================================================================
// TYPES
// ============================================================================

interface GuestSectionProps {
  guests: BookingGuestRequest[];
  onGuestsChange: (guests: BookingGuestRequest[]) => void;
  linkedGuestId: string;
  onLinkedGuestIdChange: (id: string) => void;
  fieldErrors: Record<string, string>;
  bookingSource: string;
  childAgeLimit?: number;
  infantAgeLimit?: number;
}

const TITLE_OPTIONS: SelectOption[] = [
  { value: 'Mr', label: 'Mr' }, { value: 'Mrs', label: 'Mrs' }, { value: 'Ms', label: 'Ms' },
  { value: 'Dr', label: 'Dr' }, { value: 'Prof', label: 'Prof' },
];

const GUEST_TYPE_OPTIONS: SelectOption[] = [
  { value: 'Adult', label: 'Adult' }, { value: 'Child', label: 'Child' }, { value: 'Infant', label: 'Infant' },
];

// ============================================================================
// COMPONENT
// ============================================================================

const GuestSection: React.FC<GuestSectionProps> = ({
  guests, onGuestsChange, linkedGuestId, onLinkedGuestIdChange, fieldErrors, bookingSource, childAgeLimit, infantAgeLimit,
}) => {
  const maxChildAge = (childAgeLimit ?? 12) - 1;   // e.g., childAgeLimit=12 → max age 11
  const maxInfantAge = (infantAgeLimit ?? 5) - 1;   // e.g., infantAgeLimit=5  → max age 4
  const guestRequired = isGuestNameRequired(bookingSource);
  const primaryGuest = guests[0] || { guestType: 'Adult', title: '', firstName: '', lastName: '', isChargeable: true, isPrimary: true };
  const additionalGuests = guests.slice(1);

  // --- Guest search dropdown ---
  const loadGuestOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    try {
      const res = await guestService.getAll({ searchTerm: inputValue || undefined, pageSize: inputValue ? 50 : 500, includeInactive: false });
      return (res.data || []).map((g: any) => ({
        value: g.id,
        label: `${g.title ? g.title + ' ' : ''}${g.firstName} ${g.lastName || ''} ${g.phone ? `(${g.phone})` : ''}`.trim(),
      }));
    } catch (err) {
      console.error('Guest search failed:', err);
      return [];
    }
  }, []);

  const handleLinkedGuestSelect = useCallback(async (val: string | string[] | null) => {
    const guestId = (val as string) || '';
    onLinkedGuestIdChange(guestId);

    if (guestId) {
      try {
        const res = await guestService.getById(guestId);
        if (res.success && res.data) {
          const g = res.data as any;
          const updated = [...guests];
          updated[0] = {
            ...updated[0],
            title: g.title || '',
            firstName: g.firstName || '',
            lastName: g.lastName || '',
            phone: g.phone || '',
            email: g.email || '',
            guestId: guestId,
          };
          onGuestsChange(updated);
        }
      } catch { /* ignore */ }
    } else {
      // Cleared — reset primary guest fields
      const updated = [...guests];
      updated[0] = {
        ...updated[0],
        title: '',
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        guestId: undefined,
      };
      onGuestsChange(updated);
    }
  }, [guests, onGuestsChange, onLinkedGuestIdChange]);

  // --- Update primary guest ---
  const handlePrimaryChange = useCallback((updates: Partial<BookingGuestRequest>) => {
    const updated = [...guests];
    updated[0] = { ...updated[0], ...updates };
    onGuestsChange(updated);
  }, [guests, onGuestsChange]);

  // --- Update additional guest ---
  const handleAdditionalChange = useCallback((idx: number, updates: Partial<BookingGuestRequest>) => {
    const updated = [...guests];
    updated[idx + 1] = { ...updated[idx + 1], ...updates };
    onGuestsChange(updated);
  }, [guests, onGuestsChange]);

  // --- Add guest ---
  const handleAddGuest = useCallback(() => {
    onGuestsChange([...guests, {
      guestType: 'Adult', title: '', firstName: '', lastName: '',
      isChargeable: true, isPrimary: false,
    }]);
  }, [guests, onGuestsChange]);

  // --- Remove guest ---
  const handleRemoveGuest = useCallback((idx: number) => {
    onGuestsChange(guests.filter((_, i) => i !== idx + 1));
  }, [guests, onGuestsChange]);

  return (
    <div>
      <hr className="booking-section-divider" />
      <div className="booking-section-title">
        <IconSystem.user size={15} />
        Guest Details
      </div>

      {/* Link to existing guest */}
      <div style={{ marginBottom: '0.75rem' }}>
        <SyutSelect
          label="Search Existing Guest"
          isAsync
          loadOptions={loadGuestOptions}
          defaultOptions={true}
          cacheOptions={false}
          value={linkedGuestId}
          onChange={handleLinkedGuestSelect}
          placeholder="Type name or phone to search..."
          size="md"
          isClearable
          menuPortalTarget={document.body}
        />
      </div>

      {/* Primary guest fields */}
      <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.375rem', textTransform: 'uppercase' }}>
        Primary Guest
      </div>
      {/* Title gets a narrower column; First/Last Name fill the rest. Phone +
          Email sit on their own row beneath. Inline grid (rather than
          form-grid-N) so the narrow Title column stays narrow at all widths. */}
      <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 1fr', gap: '0.5rem 0.75rem' }}>
        <div>
          <SyutSelect
            label="Title"
            options={TITLE_OPTIONS}
            value={primaryGuest.title || ''}
            onChange={(val) => handlePrimaryChange({ title: (val as string) || '' })}
            size="md"
            isClearable
            placeholder="—"
            menuPortalTarget={document.body}
          />
        </div>
        <div>
          <label className="form-label">First Name {guestRequired && <span className="text-danger">*</span>}</label>
          <input
            type="text"
            className={`form-input${fieldErrors['guestFirstName'] ? ' form-field-error' : ''}`}
            value={primaryGuest.firstName}
            onChange={e => handlePrimaryChange({ firstName: e.target.value })}
            placeholder="First name"
            maxLength={50}
          />
          {fieldErrors['guestFirstName'] && <div className="form-error-message">{fieldErrors['guestFirstName']}</div>}
        </div>
        <div>
          <label className="form-label">Last Name</label>
          <input
            type="text"
            className="form-input"
            value={primaryGuest.lastName || ''}
            onChange={e => handlePrimaryChange({ lastName: e.target.value })}
            placeholder="Last name"
            maxLength={50}
          />
        </div>
      </div>

      <div className="form-grid-2" style={{ marginTop: '0.5rem' }}>
        <div>
          <label className="form-label">Mobile Number</label>
          <input
            type="tel"
            className={`form-input${fieldErrors['guestPhone'] ? ' form-field-error' : ''}`}
            value={primaryGuest.phone || ''}
            onChange={e => handlePrimaryChange({ phone: e.target.value })}
            placeholder="+91 98xxxxxxxx"
            maxLength={20}
          />
          {fieldErrors['guestPhone'] && <div className="form-error-message">{fieldErrors['guestPhone']}</div>}
        </div>
        <div>
          <label className="form-label">Email</label>
          <input
            type="email"
            className={`form-input${fieldErrors['guestEmail'] ? ' form-field-error' : ''}`}
            value={primaryGuest.email || ''}
            onChange={e => handlePrimaryChange({ email: e.target.value })}
            placeholder="guest@example.com"
            maxLength={100}
          />
          {fieldErrors['guestEmail'] && <div className="form-error-message">{fieldErrors['guestEmail']}</div>}
        </div>
      </div>

      {/* Additional guests */}
      {additionalGuests.length > 0 && (
        <div style={{ marginTop: '0.75rem' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.375rem', textTransform: 'uppercase' }}>
            Additional Guests
          </div>
          {/* Column header — matches grid of data rows, hidden on narrow screens */}
          <div className="additional-guest-header">
            <span>Type</span>
            <span>Title</span>
            <span>First Name</span>
            <span>Last Name</span>
            <span /> {/* spacer for remove btn */}
          </div>
          {additionalGuests.map((guest, idx) => (
            <div key={idx} className={`additional-guest-row${guest.guestType === 'Child' || guest.guestType === 'Infant' ? ' has-age' : ''}`}>
              <div className="additional-guest-field additional-guest-type">
                <SyutSelect
                  options={GUEST_TYPE_OPTIONS}
                  value={guest.guestType}
                  onChange={(val) => {
                    const newType = (val as string) || 'Adult';
                    const updates: Partial<BookingGuestRequest> = { guestType: newType };
                    if (newType === 'Adult') {
                      updates.age = undefined;
                    } else if (newType === 'Infant' && guest.age !== undefined && guest.age > maxInfantAge) {
                      updates.age = maxInfantAge;
                    } else if (newType === 'Child' && guest.age !== undefined && guest.age > maxChildAge) {
                      updates.age = maxChildAge;
                    }
                    handleAdditionalChange(idx, updates);
                  }}
                  size="md"
                  isClearable={false}
                  menuPortalTarget={document.body}
                />
              </div>
              <div className="additional-guest-field additional-guest-title">
                <SyutSelect
                  options={TITLE_OPTIONS}
                  value={guest.title || ''}
                  onChange={(val) => handleAdditionalChange(idx, { title: (val as string) || '' })}
                  size="md"
                  isClearable
                  placeholder="—"
                  menuPortalTarget={document.body}
                />
              </div>
              <div className="additional-guest-field additional-guest-name">
                <input
                  type="text"
                  className="form-input"
                  value={guest.firstName}
                  onChange={e => handleAdditionalChange(idx, { firstName: e.target.value })}
                  placeholder="First name"
                  maxLength={50}
                />
              </div>
              <div className="additional-guest-field additional-guest-name">
                <input
                  type="text"
                  className="form-input"
                  value={guest.lastName || ''}
                  onChange={e => handleAdditionalChange(idx, { lastName: e.target.value })}
                  placeholder="Last name"
                  maxLength={50}
                />
              </div>
              {(guest.guestType === 'Child' || guest.guestType === 'Infant') && (
                <div className="additional-guest-field additional-guest-age">
                  <input
                    type="number"
                    className="form-input"
                    value={guest.age ?? ''}
                    onChange={e => {
                      const raw = parseInt(e.target.value);
                      if (isNaN(raw)) { handleAdditionalChange(idx, { age: undefined }); return; }
                      const ageMax = guest.guestType === 'Infant' ? maxInfantAge : maxChildAge;
                      const clamped = Math.max(0, Math.min(raw, ageMax));
                      handleAdditionalChange(idx, { age: clamped });
                    }}
                    min={0}
                    max={guest.guestType === 'Infant' ? maxInfantAge : maxChildAge}
                    placeholder="Age"
                  />
                </div>
              )}
              <button
                className="btn-icon additional-guest-remove"
                onClick={() => handleRemoveGuest(idx)}
                title="Remove guest"
              >
                <IconSystem.trash size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add guest button */}
      <button
        className="btn btn-secondary"
        onClick={handleAddGuest}
        style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}
      >
        <IconSystem.add size={14} />
        Add Guest
      </button>
    </div>
  );
};

export default GuestSection;
